/**
 * ConversationErrorBoundary Component
 * 
 * Specialized error boundary for conversation-related errors
 * Provides conversation-specific error handling and recovery
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { ErrorState } from '../ErrorState';
import { useChatStore } from '../../../stores/chatStore';

interface ConversationErrorBoundaryProps {
  children: ReactNode;
  conversationId: string;
  fallback?: ReactNode;
}

interface ConversationErrorFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
  conversationId: string;
}

function ConversationErrorFallback({ 
  error, 
  resetErrorBoundary, 
  conversationId 
}: ConversationErrorFallbackProps) {
  const handleRetry = () => {
    // Just reset the error boundary - let TanStack Query handle state
    resetErrorBoundary();
  };

  const getErrorMessage = (error: Error) => {
    if (error.message.includes('401')) {
      return 'You are not authorized to view this conversation. Please check your permissions.';
    }
    if (error.message.includes('404')) {
      return 'This conversation could not be found. It may have been deleted or moved.';
    }
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    if (error.message.includes('timeout')) {
      return 'The request timed out. The server might be busy.';
    }
    return error.message || 'An unexpected error occurred while loading the conversation.';
  };

  const getErrorTitle = (error: Error) => {
    if (error.message.includes('401')) {
      return 'Access Denied';
    }
    if (error.message.includes('404')) {
      return 'Conversation Not Found';
    }
    if (error.message.includes('network') || error.message.includes('fetch')) {
      return 'Connection Error';
    }
    if (error.message.includes('timeout')) {
      return 'Request Timeout';
    }
    return 'Conversation Error';
  };

  return (
    <ErrorState
      title={getErrorTitle(error)}
      message={getErrorMessage(error)}
      onRetry={handleRetry}
      retryLabel="Try Again"
    />
  );
}

export function ConversationErrorBoundary({ 
  children, 
  conversationId, 
  fallback 
}: ConversationErrorBoundaryProps) {
  // Create a custom error boundary specifically for conversations
  return (
    <ErrorBoundary
      fallback={fallback}
      onError={(error, errorInfo) => {
        // Log conversation-specific error info
        console.error(`Conversation Error [${conversationId}]:`, error, errorInfo);
        
        // Could integrate with error reporting service here
        if (process.env.NODE_ENV === 'production') {
          // reportError(error, { conversationId, ...errorInfo });
        }
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

export type { ConversationErrorBoundaryProps };